import { FileDown, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BizminerHeatmapSegment } from "@/types/serializers";
import {
  BENCHMARK_LABELS,
  type BenchmarkMode,
} from "../benchmarkMode";

function SegmentSelector({
  segments,
  currentSegment,
  onSelectSegment,
}: {
  segments: BizminerHeatmapSegment[];
  currentSegment: string;
  onSelectSegment: (slug: string) => void;
}) {
  if (segments.length <= 1) return null;

  return (
    <Select value={currentSegment} onValueChange={onSelectSegment}>
      <SelectTrigger className="w-[200px] h-10">
        <SelectValue placeholder="Select segment" />
      </SelectTrigger>
      <SelectContent>
        {segments.map((seg) => (
          <SelectItem key={seg.slug} value={seg.slug}>
            {seg.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AccountSelector({
  accounts,
  selectedAccountValue,
  onSelectAccount,
}: {
  accounts: Array<{ id: number; name: string }>;
  selectedAccountValue: string;
  onSelectAccount: (value: string) => void;
}) {
  if (accounts.length <= 1) return null;

  return (
    <Select value={selectedAccountValue} onValueChange={onSelectAccount}>
      <SelectTrigger className="w-[200px] h-10">
        <SelectValue placeholder="Select account" />
      </SelectTrigger>
      <SelectContent>
        {accounts.map((c) => (
          <SelectItem key={c.id} value={String(c.id)}>
            {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function HeatmapHeader({
  title,
  subtitle,
  tileScaleLabel,
  total,
  maxTotal,
  selectedAccountValue,
  accounts,
  onSelectAccount,
  bestPerformerDisabled,
  onSelectBestPerformer,
  benchmarkMode,
  onBenchmarkChange,
  benchmarkLoading,
  exportDisabled,
  exporting,
  onExportPptx,
  segments,
  currentSegment,
  onSelectSegment,
}: {
  title: string;
  subtitle: string;
  tileScaleLabel: string;
  total: number | null;
  maxTotal: number;
  selectedAccountValue: string;
  accounts: Array<{ id: number; name: string }>;
  onSelectAccount: (value: string) => void;
  bestPerformerDisabled: boolean;
  onSelectBestPerformer: () => void;
  benchmarkMode: BenchmarkMode;
  onBenchmarkChange: (mode: BenchmarkMode) => void;
  benchmarkLoading: boolean;
  exportDisabled: boolean;
  exporting: boolean;
  onExportPptx: () => void;
  segments?: BizminerHeatmapSegment[];
  currentSegment?: string;
  onSelectSegment?: (slug: string) => void;
}) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between pb-6 border-b border-border/60">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{title}</h1>
          <Badge className="tabular-nums" variant="secondary">
            Total {total != null ? total : "—"} / {maxTotal}
          </Badge>
        </div>
        <p className="text-muted-foreground truncate whitespace-nowrap">
          {subtitle}
        </p>
        <p className="text-xs text-muted-foreground mt-1">{tileScaleLabel}</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap justify-end">
        {segments && onSelectSegment && (
          <SegmentSelector
            segments={segments}
            currentSegment={currentSegment || ""}
            onSelectSegment={onSelectSegment}
          />
        )}

        <AccountSelector
          accounts={accounts}
          selectedAccountValue={selectedAccountValue}
          onSelectAccount={onSelectAccount}
        />

        <Button
          type="button"
          variant="outline"
          className="h-10"
          onClick={onSelectBestPerformer}
          disabled={bestPerformerDisabled}
        >
          Best performer
        </Button>

        <Select
          value={benchmarkMode}
          onValueChange={(value) => onBenchmarkChange(value as BenchmarkMode)}
          disabled={benchmarkLoading}
        >
          <SelectTrigger className="w-[160px] h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(BENCHMARK_LABELS) as BenchmarkMode[]).map((mode) => (
              <SelectItem key={mode} value={mode}>
                {BENCHMARK_LABELS[mode]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          type="button"
          variant="default"
          className="h-10 w-[140px]"
          onClick={onExportPptx}
          disabled={exportDisabled}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileDown className="h-4 w-4 mr-2" />
          )}
          Export PPTX
        </Button>
      </div>
    </div>
  );
}
